import React, { useState, useEffect, useRef } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import coinIcon from "../../images/site_logo/TC-Logo-icon.png";
import "./style.css";
import { API_URL } from "../../constant";
import ReCAPTCHA from "react-google-recaptcha";

function RetailAgent(props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [showStatusPopup, setShowStatusPopup] = useState(false);
  const [userStatus, setUserStatus] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [loginData, setLoginData] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null); // recaptcha

  // Ref for the auto-dismiss timeout
  const autoDismissRef = useRef(null);

  // captcha handler
  const onCaptchaChange = (token) => {
      setCaptchaToken(token);
  };

  const validateLoginForm = () => {
    if (!email.trim()) {
      setMessage({
        text: "Email is required.",
        type: "error",
      });
      return false;
    }

    if (email.length > 255) {
      setMessage({ text: "Please enter a valid email address.", type: "error" });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage({
        text: "Please enter a valid email address.",
        type: "error",
      });
      return false;
    }

    if (!password) {
      setMessage({
        text: "Password is required.",
        type: "error",
      });
      return false;
    }

    if (password.length < 8) {
      setMessage({ text: "Email or Password incorrect.", type: "error" });
      return false;
    }

    if (password.length > 50) {
      setMessage({ text: "Email or Password incorrect.", type: "error" });
      return false;
    }

    if (!captchaToken) {
      setMessage({
        text: "Captcha verification is required.",
        type: "error",
      });
      return false;
    }

    return true;
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    // Check reCAPTCHA
        // if (!captchaToken) {
        //   setMessage({
        //       text: 'Please verify that you are not a robot.',
        //       type: 'error'
        //   });
        //   return;
        // }

    // Frontend validation
    if (!validateLoginForm()) return;

    // Clear any existing auto-dismiss timeout
    if (autoDismissRef.current) {
      clearTimeout(autoDismissRef.current);
      autoDismissRef.current = null;
    }

    setIsLoading(true);
    // setMessage({ text: "", type: "" });

    try {
      const response = await fetch(`${API_URL}/api/agent/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
          password: password,
          "g-recaptcha-response": captchaToken, // send captcha token
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Login failed. Please try again.");
      }

      // Login successful
      localStorage.setItem("auth_token", data.access_token);
      localStorage.setItem("user_type", "agent");
      setLoginData(data);

      setMessage({
        text: data.message || "Login successful!",
        type: "success",
      });

      // Get user status after successful login
      const statusResult = await getUserStatus(data.access_token);

      // Handle different statuses
      handleUserStatus(statusResult, data.access_token);
    } catch (error) {
      console.error("Login error:", error);

      setMessage({
        text: error.message || "Network error. Please try again.",
        type: "error",
      });

      // Clear auth data on error
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user_type");
      setLoginData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getUserStatus = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/agent/status`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUserStatus(data.user_status);
        return data.user_status;
      } else {
        console.error("Failed to get user status:", response.status);
        return null;
      }
    } catch (error) {
      console.error("Error fetching user status:", error);
      return null;
    }
  };

  const openInNewTab = (url) => {
    try {
      const newWindow = window.open(url, "_blank", "noopener,noreferrer");

      if (newWindow) {
        return newWindow;
      }

      // Fallback for browsers that block window.open
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return { closed: true };
    } catch (error) {
      console.error("Error opening new tab:", error);
      window.location.href = url;
      return { closed: true };
    }
  };

  const handleUserStatus = (status, token) => {
    // If status is null (meaning getUserStatus failed), show error
    if (status === null) {
      setMessage({
        text: "Unable to verify account status. Please try again.",
        type: "error",
      });
      // Clear auth data on error
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user_type");
      setLoginData(null);
      return;
    }

    switch (status) {
      case "active":
      case "approved":
        // Generate a token key for cross-tab communication
        const tempTokenKey = `agent_temp_token_${Date.now()}`;
        localStorage.setItem(tempTokenKey, token);

        // Build wallet URL with token parameter
        const walletUrl = `${window.location.origin}/retailer-business-agent/wallet`;

        // Open wallet in new tab
        const newWindow = openInNewTab(walletUrl);

        // Set a timeout to send message to new tab
        setTimeout(() => {
          try {
            if (newWindow && newWindow.postMessage) {
              newWindow.postMessage(
                {
                  type: "LOGIN_SUCCESS",
                  token: token,
                  tempTokenKey: tempTokenKey,
                },
                window.location.origin
              );
            }
          } catch (e) {
            console.log("Could not send message to wallet tab:", e);
          }

          // Clean up temp token after 30 seconds
          setTimeout(() => {
            localStorage.removeItem(tempTokenKey);
          }, 30000);
        }, 500);

        // Clear form fields
        setEmail("");
        setPassword("");

        // Set auto-dismiss timeout for success message
        autoDismissRef.current = setTimeout(() => {
          setMessage({ text: "", type: "" });
        }, 15000); // 15 seconds
        break;

      case "awaiting":
      case "pending":
        setPopupMessage(
          "Your agent profile is currently under review by our team. This process usually takes 24-48 hours. We will notify you via email once your account has been approved."
        );
        setShowStatusPopup(true);
        break;

      case "suspended":
        setPopupMessage(
          "Your agent account has been suspended. Please contact support for more information."
        );
        setShowStatusPopup(true);
        break;

      case "rejected":
        setPopupMessage(
          "Your agent application has been rejected. Please contact support for more information."
        );
        setShowStatusPopup(true);
        break;

      default:
        setPopupMessage(
          "Your account status is currently being reviewed. Please contact support for assistance."
        );
        setShowStatusPopup(true);
        break;
    }
  };

  const handleOpenPortal = () => {
    if (
      loginData &&
      loginData.access_token &&
      userStatus !== "awaiting" &&
      userStatus !== "pending" &&
      userStatus !== "suspended" &&
      userStatus !== "rejected"
    ) {
      // Reopen portal with stored token
      const tempTokenKey = `agent_temp_token_${Date.now()}`;
      localStorage.setItem(tempTokenKey, loginData.access_token);
      const portalUrl = `${window.location.origin}/retailer-business-agent/wallet`;
      openInNewTab(portalUrl);
    }
  };

  const handleClosePopup = () => {
    setShowStatusPopup(false);
    // If status is not approved, log them out
    if (userStatus !== "active" && userStatus !== "approved") {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user_type");
      setLoginData(null);
      setEmail("");
      setPassword("");
    }
  };

  const handleDismissMessage = () => {
    // Clear the auto-dismiss timeout if it exists
    if (autoDismissRef.current) {
      clearTimeout(autoDismissRef.current);
      autoDismissRef.current = null;
    }
    setMessage({ text: "", type: "" });
  };

  // Clean up timeout on component unmount
  useEffect(() => {
    return () => {
      if (autoDismissRef.current) {
        clearTimeout(autoDismissRef.current);
      }
    };
  }, []);

  const messageStyle = {
    padding: "10px 15px",
    borderRadius: "8px",
    marginBottom: "15px",
  };

  // Success Message with Portal Button Component
  const SuccessMessage = () => {
    if (
      message.type !== "success" ||
      userStatus === "awaiting" ||
      userStatus === "pending" ||
      userStatus === "suspended" ||
      userStatus === "rejected"
    )
      return null;

    return (
      <div
        style={{
          ...messageStyle,
          backgroundColor: "#e8f5e9",
          color: "#2e7d32",
          border: "1px solid #c8e6c9",
          textAlign: "center",
        }}
      >
        <p style={{ margin: "0 0 10px 0" }}>
          {message.text}
          <button
            onClick={handleDismissMessage}
            style={{
              background: "none",
              border: "none",
              color: "inherit",
              marginLeft: "10px",
              cursor: "pointer",
              float: "right",
              fontSize: "16px",
            }}
            title="Dismiss message"
          >
            ×
          </button>
        </p>
        <button
          onClick={handleOpenPortal}
          style={{
            backgroundColor: "#2D4CA8",
            color: "white",
            border: "none",
            padding: "10px 20px",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "bold",
            fontSize: "14px",
            marginTop: "5px",
          }}
        >
          Open Agent Portal in New Tab
        </button>
      </div>
    );
  };

  // Enhanced Status Popup Component
  const StatusPopup = () => {
    if (!showStatusPopup) return null;

    const getPopupConfig = () => {
      switch (userStatus) {
        case "awaiting":
        case "pending":
          return {
            icon: "⏳",
            title: "Profile Under Review",
            bgColor: "#FFF3CD",
            borderColor: "#FFC107",
            iconColor: "#856404",
            titleColor: "#856404",
          };
        case "suspended":
          return {
            icon: "⚠️",
            title: "Account Suspended",
            bgColor: "#F8D7DA",
            borderColor: "#F5C6CB",
            iconColor: "#721C24",
            titleColor: "#721C24",
          };
        case "rejected":
          return {
            icon: "❌",
            title: "Application Rejected",
            bgColor: "#F8D7DA",
            borderColor: "#F5C6CB",
            iconColor: "#721C24",
            titleColor: "#721C24",
          };
        default:
          return {
            icon: "ℹ️",
            title: "Account Status",
            bgColor: "#D1ECF1",
            borderColor: "#BEE5EB",
            iconColor: "#0C5460",
            titleColor: "#0C5460",
          };
      }
    };

    const config = getPopupConfig();

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            padding: "30px",
            borderRadius: "15px",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
            maxWidth: "500px",
            width: "90%",
            textAlign: "center",
            position: "relative",
          }}
        >
          <button
            onClick={handleClosePopup}
            style={{
              position: "absolute",
              top: "15px",
              right: "15px",
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#666",
            }}
          >
            ×
          </button>

          <div style={{ marginBottom: "20px" }}>
            <div
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                backgroundColor: config.bgColor,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                margin: "0 auto 20px",
                border: `3px solid ${config.borderColor}`,
              }}
            >
              <span style={{ fontSize: "36px", color: config.iconColor }}>
                {config.icon}
              </span>
            </div>
            <h3 style={{ color: config.titleColor, marginBottom: "15px" }}>
              {config.title}
            </h3>
          </div>

          <p style={{ color: "#666", lineHeight: "1.6", marginBottom: "25px" }}>
            {popupMessage}
          </p>

          {(userStatus === "awaiting" || userStatus === "pending") && (
            <div
              style={{
                backgroundColor: "#E7F3FF",
                padding: "15px",
                borderRadius: "8px",
                marginBottom: "20px",
                border: "1px solid #B3D9FF",
              }}
            >
              <p style={{ color: "#004085", margin: 0, fontSize: "14px" }}>
                <strong>What's next?</strong> Once approved, you'll gain full
                access to your agent dashboard where you can start onboarding
                retail businesses, track your commissions, and access marketing
                materials.
              </p>
            </div>
          )}

          <button
            onClick={handleClosePopup}
            style={{
              backgroundColor: "#2D4CA8",
              color: "white",
              border: "none",
              padding: "12px 30px",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: "16px",
            }}
          >
            Understand
          </button>
        </div>
      </div>
    );
  };

  return (
    <section
      id="id_ico_about_section"
      className={`bg-white justify-content-center d-flex align-items-center ${props.abClass}`}
      style={{ color: "#000" }}
    >
      <div className="container mt-5 mb-5">
        <div className="row justify-content-center align-items-center">
          {/* Text Content Section */}
          <div className="col-lg-7 mb-5 mb-lg-0 mt-5">
            <div>
              <h1
                className="hero_title mb-3"
                data-aos="fade-up"
                data-aos-duration="800"
                style={{ color: "#000" }}
              >
                Welcome to TopCoin UK - Become a Retail Business Agent
              </h1>
              <p className="mb-2">
                As a Retail Business Agent with TopCoin UK, you help retail
                businesses join our loyalty and rewards platform. In return, you
                receive referral-based rewards for successful introductions, in
                line with our programme terms.
              </p>
              <p className="mb-4">
                Your role focuses on introducing and supporting businesses, not
                providing financial or investment services.
              </p>

              <h3
                className="mb-3"
                data-aos="fade-up"
                data-aos-duration="800"
                style={{ color: "#000" }}
              >
                What You Do as a Retail Business Agent
              </h3>

              <ul className="mb-4">
                <li>
                  Introduce retail businesses to the TopCoin UK loyalty platform
                </li>
                <li>Support businesses during the onboarding process</li>
                <li>Help explain how the loyalty rewards system works</li>
                <li>
                  Build and manage your portfolio of introduced businesses
                </li>
              </ul>

              <h3
                className="mb-3"
                data-aos="fade-up"
                data-aos-duration="800"
                style={{ color: "#000" }}
              >
                Agent Benefits & Features
              </h3>

              <ul className="mb-4">
                <li>
                  Dedicated Agent Dashboard
                  <p className="mb-1">
                    Monitor your activity, introductions, and referral rewards
                    in one place.
                  </p>
                </li>
                <li>
                  Marketing Materials
                  <p className="mb-1">
                    Access approved promotional tools and resources to support
                    business introductions.
                  </p>
                </li>
                <li>
                  Training & Support
                  <p className="mb-1">
                    Receive guidance, onboarding assistance, and ongoing support
                    from the TopCoin UK team.
                  </p>
                </li>
                <li>Perf</li>
              </ul>

              <h3
                className="mb-3"
                data-aos="fade-up"
                data-aos-duration="800"
                style={{ color: "#000" }}
              >
                How to Get Started:
              </h3>

              <ul className="mb-4">
                <li>
                  Complete your agent registration and training
                  <p className="mb-1">
                    Sign up as a Retail Business Agent and complete the required
                    onboarding and guidance.
                  </p>
                </li>
                <li>
                  Access your agent portal and marketing materials
                  <p className="mb-1">
                    Use your dedicated portal to access approved marketing
                    resources and tools.
                  </p>
                </li>
                <li>
                  Connect with retail businesses in your area
                  <p className="mb-1">
                    Introduce local retail businesses to the TopCoin UK loyalty
                    and rewards platform.
                  </p>
                </li>
                <li>
                  Support business onboarding using your unique referral code
                  <p className="mb-1">
                    Assist businesses as they join the platform using your
                    assigned referral identifier.
                  </p>
                </li>
                <li>
                  Receive referral-based rewards linked to participating
                  businesses
                  <p className="mb-1">
                    Referral rewards are issued in accordance with programme
                    terms when businesses you introduce actively
                    participate.Submit your application and complete our
                    onboarding process to become a certified Retail Business
                    Agent.
                  </p>
                </li>
                <li>
                  Build ongoing referral activity over time
                  <p className="mb-1">
                    Build ongoing referral activity over timeSubmit your
                    application and complete our onboarding process to become a
                    certified Retail Business Agent.
                  </p>
                </li>
              </ul>

              <h5
                className="mb-3"
                data-aos="fade-up"
                data-aos-duration="800"
                style={{ color: "#000" }}
              >
                Important Notice
              </h5>
              <p className="mb-4">
                TopCoin UK is a loyalty and referral platform. It is not an
                investment opportunity, financial product, or regulated
                financial service. Participation as an agent does not involve
                providing financial advice or regulated activities.
              </p>
            </div>
          </div>

          {/* Login Form Section */}
          <div className="col-lg-4 position-relative">
            <div
              className="bg-white text-center shadow p-4"
              style={{
                color: "#000",
                borderRadius: "20px",
              }}
            >
              <div className="d-flex justify-content-center mb-0">
                <img
                  src={coinIcon}
                  alt="TopCoin UK"
                  style={{ width: 115, height: 115 }}
                />
              </div>

              <h4
                className="fw-bold mb-1"
                style={{
                  background:
                    "linear-gradient(90deg, #2D4CA8 52%, #9F4AFF 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  display: "inline-block",
                  fontSize: "1.2rem",
                }}
              >
                {/* TopCoin UK
                <br />
                Retail Business Agent
                <br /> Portal */}
                Retail Business Agents – Login
              </h4>
              <h3 className="sub-title mb-2">Enter your Email and Password</h3>

              {/* Success Message with Portal Button */}
              <SuccessMessage />

              {/* Regular Message display */}
              {message.text && message.type !== "success" && (
                <div
                  style={{
                    ...messageStyle,
                    backgroundColor:
                      message.type === "error" ? "#ffebee" : "#e8f5e9",
                    color: message.type === "error" ? "#c62828" : "#2e7d32",
                    border:
                      message.type === "error"
                        ? "1px solid #ffcdd2"
                        : "1px solid #c8e6c9",
                  }}
                >
                  {message.text}
                  <button
                    onClick={handleDismissMessage}
                    style={{
                      background: "none",
                      border: "none",
                      color: "inherit",
                      marginLeft: "10px",
                      cursor: "pointer",
                      float: "right",
                      fontSize: "0.9rem",
                    }}
                    title="Dismiss message"
                  >
                    ×
                  </button>
                </div>
              )}

              {/* Login Form */}
              <form onSubmit={handleLogin} className="text-start mb-4">
                <div className="mb-1">
                  <label
                    className="form-label fw-semibold"
                    style={{ color: "#052C65", fontSize: "0.9rem" }}
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    maxLength={255}
                    className="form-control bg-white"
                    placeholder="Your registered agent email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    style={{
                      borderRadius: "8px",
                      padding: "8px",
                      border: "1px solid #ccc",
                      color: "black",
                    }}
                  />
                </div>

                <div className="mb-0 position-relative">
                  <label
                    className="form-label fw-semibold mb-0"
                    style={{ color: "#052C65", fontSize: "0.9rem"  }}
                  >
                    Password
                  </label>
                  <div className="position-relative">
                    <input
                      type={passwordVisible ? "text" : "password"}
                      maxLength={50}
                      className="form-control bg-white"
                      placeholder="Your agent account password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      style={{
                        borderRadius: "8px",
                        padding: "8px",
                        border: "1px solid #ccc",
                        color: "black",
                        paddingRight: "40px",
                      }}
                    />
                    <span
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      style={{
                        position: "absolute",
                        top: "50%",
                        right: "12px",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#666",
                        zIndex: 2,
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {passwordVisible ? (
                        <FaEye size={18} />
                      ) : (
                        <FaEyeSlash size={18} />
                      )}
                    </span>
                  </div>
                </div>

                <div className="mb-2 text-end">
                  <a
                    href="/forgot-password-a"
                    style={{
                      color: "#2D4CA8",
                      fontWeight: "500",
                      textDecoration: "none",
                      fontSize: "0.9rem",
                    }}
                  >
                    Forgot Password?
                  </a>
                </div>

                {/* reCAPTCHA */}
                <div style={{ marginBottom: "16px", 
                              display: "flex", 
                              alignItems: "center", 
                              flexDirection: "column",
                              transform: "scale(0.77)", 
                            }}
                >
                    <ReCAPTCHA
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} // replace with your actual site key
                        onChange={onCaptchaChange}
                    />
                    {/* { !captchaToken && message.type === "error" && message.text.includes("robot") && (
                        <span style={{ ...errorStyle, display: "block", marginTop: "8px" }}>
                            {message.text}
                        </span>
                    )} */}
                </div>

                <button
                  type="submit"
                  className="w-100 text-white fw-bold py-2"
                  disabled={isLoading}
                  style={{
                    backgroundColor: isLoading ? "#666" : "#0000A0",
                    borderRadius: "8px",
                    border: "none",
                    opacity: isLoading ? 0.7 : 1,
                    cursor: isLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {isLoading ? "Logging in..." : "Login"}
                </button>
              </form>

              {/* <div className="mt-4">
                <p className="mb-0" style={{ color: "#666" }}>
                  Interested in becoming an Agent?{" "}
                  <a
                    href="/retail-agent-signup"
                    target="_blank"
                    style={{
                      color: "#2D4AFF",
                      textDecoration: "none",
                      fontWeight: "bold",
                    }}
                  >
                    Apply as Retail Business Agent
                  </a>
                </p>
              </div> */}
            </div>
          </div>
        </div>
      </div>

      {/* Status Popup */}
      <StatusPopup />
      <style>{`
          iframe[title="reCAPTCHA"] {
            min-height: 100px !important;
          }
      `}</style>
    </section>
  );
}

export default RetailAgent;
